import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useSite } from '../../context/SiteContext';
import { toast } from 'react-hot-toast';
import { parseEmployeeCsv } from '../../utils/employeeCsvImport';

const Employees = () => {
  const { isAuthenticated } = useAuth();
  const { getCurrentSiteId, currentSite } = useSite();
  
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importRows, setImportRows] = useState([]);
  const [importFileName, setImportFileName] = useState('');
  const [defaultPassword, setDefaultPassword] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [form, setForm] = useState({
    employee_number: '',
    name: '',
    password: '',
    has_access: false,
    role: 'operator_conversion',
    position: '',
    active: true
  });

  const load = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      
      const siteId = getCurrentSiteId();
      const params = { page, per_page: 50, user_type: 'employee' };
      if (siteId) {
        params.site_id = siteId;
      }
      if (search) {
        params.search = search;
      }
      
      const { data } = await axios.get('/employees', { params });
      setEmployees(data.data);
      setCurrentPage(data.current_page);
      setTotalPages(data.last_page);
    } catch (error) {
      console.error('Error loading employees:', error);
      toast.error('Error al cargar los empleados');
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  }, [getCurrentSiteId, search]);

  useEffect(() => {
    if (isAuthenticated()) {
      load(1);
    }
  }, [currentSite, isAuthenticated, load]);

  const onChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const onCreate = async () => {
    try {
      if (form.has_access && !form.password) {
        toast.error('Captura una contraseña para habilitar el acceso');
        return;
      }

      const siteId = getCurrentSiteId();
      const payload = { ...form };
      
      if (siteId) {
        payload.site_id = siteId;
      }
      
      // Only send password if it's not empty
      if (!payload.password) {
        delete payload.password;
      }
      
      await axios.post('/employees', payload);
      setForm({ employee_number: '', name: '', password: '', has_access: false, role: 'operator_conversion', position: '', active: true });
      toast.success('Empleado creado exitosamente');
      load(currentPage);
    } catch (error) {
      console.error('Error creating:', error);
      toast.error(error.response?.data?.message || 'Error al crear el empleado');
    }
  };

  const onUpdate = async () => {
    try {
      const employee = employees.find((item) => item.id === editing);
      if (form.has_access && !employee?.has_access && !form.password) {
        toast.error('Captura una contraseña nueva para habilitar el acceso');
        return;
      }

      const payload = { ...form };
      
      // Only send password if it's not empty
      if (!payload.password) {
        delete payload.password;
      }
      
      await axios.put(`/employees/${editing}`, payload);
      setEditing(null);
      setForm({ employee_number: '', name: '', password: '', has_access: false, role: 'operator_conversion', position: '', active: true });
      toast.success('Empleado actualizado exitosamente');
      load(currentPage);
    } catch (error) {
      console.error('Error updating:', error);
      toast.error(error.response?.data?.message || 'Error al actualizar el empleado');
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este empleado?')) return;
    try {
      await axios.delete(`/employees/${id}`);
      toast.success('Empleado eliminado exitosamente');
      load(currentPage);
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Error al eliminar el empleado');
    }
  };

  const onEdit = (employee) => {
    setEditing(employee.id);
    setForm({
      employee_number: employee.employee_number,
      name: employee.name,
      password: '',
      has_access: Boolean(employee.has_access),
      role: employee.role || 'operator_conversion',
      position: employee.position || '',
      active: employee.active ?? true
    });
  };

  const onCancelEdit = () => {
    setEditing(null);
    setForm({ employee_number: '', name: '', password: '', has_access: false, role: 'operator_conversion', position: '', active: true });
  };

  const closeImportModal = () => {
    if (importing) return;
    setShowImportModal(false);
    setImportRows([]);
    setImportFileName('');
    setDefaultPassword('');
    setImportResult(null);
  };

  const onImportFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      if (file.size > 2 * 1024 * 1024) throw new Error('El archivo no puede superar 2 MB.');
      const rows = parseEmployeeCsv(await file.text());
      setImportRows(rows);
      setImportFileName(file.name);
      setImportResult(null);
    } catch (error) {
      setImportRows([]);
      setImportFileName('');
      setImportResult({ parse_error: error.message });
    }
  };

  const importEmployees = async () => {
    if (!importRows.length) return;
    if (defaultPassword && defaultPassword.length < 6) {
      toast.error('La contraseña común debe tener al menos 6 caracteres');
      return;
    }
    setImporting(true);
    setImportResult(null);
    try {
      const data = importRows.map((employee) => (
        defaultPassword && !employee.password
          ? { ...employee, password: defaultPassword, has_access: true }
          : employee
      ));
      const siteId = getCurrentSiteId();
      const response = await axios.post('/employees/import', { data, ...(siteId ? { site_id: siteId } : {}) });
      setImportResult(response.data);
      await load(1);
      if (response.data.failed > 0) {
        toast.error(`${response.data.created} creados, ${response.data.skipped || 0} omitidos y ${response.data.failed} rechazados`);
      } else {
        toast.success(`${response.data.created} creados y ${response.data.skipped || 0} existentes omitidos`);
      }
    } catch (error) {
      const message = error.response?.data?.message || 'No fue posible importar los empleados';
      setImportResult({ parse_error: message });
      toast.error(message);
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    const csv = 'employee_number,name,position,active,password\n00123,Nombre completo,maquinista,true,\n';
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'plantilla_empleados.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-extrabold text-slate-900">Empleados</h1>
              <p className="text-sm text-slate-600 mt-1">Gestión de empleados para roles de conversión</p>
            </div>
            <button
              type="button"
              onClick={() => setShowImportModal(true)}
              className="rounded-lg bg-emerald-600 px-4 py-2 font-semibold text-white shadow hover:bg-emerald-700"
            >
              Importar CSV
            </button>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6 mb-6">
          <h2 className="text-lg font-bold text-slate-900 mb-4">
            {editing ? 'Editar Empleado' : 'Agregar Empleado'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 xl:grid-cols-7 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Número de Empleado</label>
              <input
                type="text"
                name="employee_number"
                value={form.employee_number}
                onChange={onChange}
                autoComplete="off"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:ring-2 focus:ring-blue-500"
                placeholder="123456"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Nombre</label>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={onChange}
                autoComplete="off"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:ring-2 focus:ring-blue-500"
                placeholder="Nombre completo"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Contraseña {editing && '(opcional)'}</label>
              <input
                type="password"
                name="password"
                value={form.password}
                onChange={onChange}
                disabled={!form.has_access}
                autoComplete="new-password"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:ring-2 focus:ring-blue-500 disabled:bg-slate-100 disabled:text-slate-400"
                placeholder={!form.has_access ? "Acceso deshabilitado" : editing ? "Dejar vacío para no cambiar" : "Mínimo 6 caracteres"}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Acceso al sistema</label>
              <select
                name="has_access"
                value={String(form.has_access)}
                onChange={(e) => setForm((previous) => ({
                  ...previous,
                  has_access: e.target.value === 'true',
                  password: e.target.value === 'true' ? previous.password : ''
                }))}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:ring-2 focus:ring-blue-500"
              >
                <option value="false">Sin acceso</option>
                <option value="true">Puede iniciar sesión</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Rol</label>
              <select
                name="role"
                value={form.role}
                onChange={onChange}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:ring-2 focus:ring-blue-500"
              >
                <option value="operator_conversion">Operador Conversión</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Puesto</label>
              <select
                name="position"
                value={form.position}
                onChange={onChange}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Sin asignar</option>
                <option value="adjustador">Ajustador</option>
                <option value="maquinista">Maquinista</option>
                <option value="recibidor">Recibidor</option>
                <option value="jefe_grupo">Jefe de grupo</option>
                <option value="otro">Otro</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Estado</label>
              <select
                name="active"
                value={form.active}
                onChange={(e) => setForm(p => ({ ...p, active: e.target.value === 'true' }))}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:ring-2 focus:ring-blue-500"
              >
                <option value="true">Activo</option>
                <option value="false">Inactivo</option>
              </select>
            </div>
          </div>
          <div className="flex items-end gap-2">
            {editing ? (
              <>
                <button
                  type="button"
                  onClick={onUpdate}
                  className="px-4 py-2 rounded-lg bg-blue-700 text-white font-semibold shadow hover:bg-blue-800 flex-1"
                >
                  Actualizar
                </button>
                <button
                  type="button"
                  onClick={onCancelEdit}
                  className="px-4 py-2 rounded-lg bg-slate-200 text-slate-700 font-semibold hover:bg-slate-300"
                >
                  Cancelar
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={onCreate}
                className="px-4 py-2 rounded-lg bg-blue-700 text-white font-semibold shadow hover:bg-blue-800 flex-1"
              >
                Crear
              </button>
            )}
          </div>
        </div>

        {/* Search */}
        <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-4 mb-6">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por número o nombre..."
              className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:ring-2 focus:ring-blue-500"
            />
            <button
              type="button"
              onClick={() => load(1)}
              className="px-4 py-2 rounded-lg bg-blue-700 text-white font-semibold shadow hover:bg-blue-800"
            >
              Buscar
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-slate-500">Cargando...</div>
          ) : employees.length === 0 ? (
            <div className="p-10 text-center text-slate-500">No hay empleados registrados</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-xs md:text-sm whitespace-nowrap">
                  <thead className="bg-slate-50 text-slate-700 sticky top-0">
                    <tr>
                      <th className="px-2 md:px-4 py-2 md:py-3 text-left font-bold w-8">ID</th>
                      <th className="px-2 md:px-4 py-2 md:py-3 text-left font-bold">Emp.</th>
                      <th className="px-2 md:px-4 py-2 md:py-3 text-left font-bold">Nombre</th>
                      <th className="px-2 md:px-4 py-2 md:py-3 text-left font-bold hidden md:table-cell">Rol</th>
                      <th className="px-2 md:px-4 py-2 md:py-3 text-left font-bold">Puesto</th>
                      <th className="px-2 md:px-4 py-2 md:py-3 text-center font-bold">Acceso</th>
                      <th className="px-2 md:px-4 py-2 md:py-3 text-center font-bold hidden sm:table-cell">Estado</th>
                      <th className="px-2 md:px-4 py-2 md:py-3 text-center font-bold">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employees.map((emp) => (
                      <tr key={emp.id} className="border-t hover:bg-slate-50">
                        <td className="px-2 md:px-4 py-2 md:py-3 text-slate-500 text-xs">{emp.id}</td>
                        <td className="px-2 md:px-4 py-2 md:py-3 font-mono font-semibold">{emp.employee_number}</td>
                        <td className="px-2 md:px-4 py-2 md:py-3">{emp.name}</td>
                        <td className="px-2 md:px-4 py-2 md:py-3 hidden md:table-cell">
                          <span className="px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                            {emp.role === 'operator_conversion' ? 'Op. Conv.' : emp.role}
                          </span>
                        </td>
                        <td className="px-2 md:px-4 py-2 md:py-3">
                          {emp.position ? (
                            <span className="px-2 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-700">
                              {emp.position === 'adjustador' && 'Ajust.'}
                              {emp.position === 'maquinista' && 'Maq.'}
                              {emp.position === 'recibidor' && 'Recib.'}
                              {emp.position === 'jefe_grupo' && 'Jefe'}
                              {emp.position === 'otro' && 'Otro'}
                            </span>
                          ) : (
                            <span className="text-slate-400 text-xs">Sin asignar</span>
                          )}
                        </td>
                        <td className="px-2 md:px-4 py-2 md:py-3 text-center">
                          {emp.has_access ? (
                            <span className="inline-flex items-center gap-1 text-green-700 text-xs font-semibold">
                              <svg className="w-3 h-3 md:w-4 md:h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" clipRule="evenodd" />
                              </svg>
                              <span className="hidden md:inline">Habilitado</span>
                            </span>
                          ) : (
                            <span className="text-slate-400 text-xs">Sin acceso</span>
                          )}
                        </td>
                        <td className="px-2 md:px-4 py-2 md:py-3 text-center hidden sm:table-cell">
                          {emp.active ? (
                            <span className="px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                              ✓
                            </span>
                          ) : (
                            <span className="px-2 py-1 rounded-full text-xs font-semibold bg-slate-200 text-slate-600">
                              ✗
                            </span>
                          )}
                        </td>
                        <td className="px-2 md:px-4 py-2 md:py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              type="button"
                              onClick={() => onEdit(emp)}
                              className="px-2 py-1 rounded text-xs md:text-sm bg-blue-100 text-blue-700 font-semibold hover:bg-blue-200"
                              title="Editar"
                            >
                              ✎
                            </button>
                            <button
                              type="button"
                              onClick={() => onDelete(emp.id)}
                              className="px-2 py-1 rounded text-xs md:text-sm bg-red-100 text-red-700 font-semibold hover:bg-red-200"
                              title="Eliminar"
                            >
                              ✕
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="border-t border-slate-200 px-4 py-3 flex items-center justify-between">
                  <div className="text-sm text-slate-600">
                    Página {currentPage} de {totalPages}
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => load(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-3 py-1 rounded-lg bg-slate-100 text-slate-700 font-semibold hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Anterior
                    </button>
                    <button
                      type="button"
                      onClick={() => load(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 rounded-lg bg-slate-100 text-slate-700 font-semibold hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Siguiente
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {showImportModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-2xl font-extrabold text-slate-900">Importar empleados</h2>
                  <p className="mt-1 text-sm text-slate-600">Los números nuevos se crean y los que ya existen se omiten.</p>
                </div>
                <button type="button" onClick={closeImportModal} className="text-2xl font-bold text-slate-400 hover:text-slate-700">×</button>
              </div>

              <div className="mt-5 rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-950">
                <div className="font-bold">Columnas CSV</div>
                <div className="mt-1 font-mono text-xs">employee_number,name,position,active,password</div>
                <div className="mt-2 text-xs">Obligatorias: employee_number y name. Puestos permitidos: ajustador, maquinista, recibidor, jefe_grupo u otro. La contraseña es opcional y debe tener al menos 6 caracteres.</div>
                <button type="button" onClick={downloadTemplate} className="mt-2 font-bold text-blue-700 underline">Descargar plantilla</button>
              </div>

              <label className="mt-5 block cursor-pointer rounded-xl border-2 border-dashed border-slate-300 p-6 text-center hover:border-blue-400 hover:bg-blue-50/40">
                <span className="block font-bold text-slate-800">Seleccionar archivo CSV</span>
                <span className="mt-1 block text-sm text-slate-500">Máximo 2 MB y 2,000 empleados</span>
                <input type="file" accept=".csv,text/csv,text/plain" onChange={onImportFile} className="sr-only" />
              </label>

              {importFileName && (
                <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-800">
                  {importFileName}: {importRows.length} filas listas
                </div>
              )}

              <div className="mt-4">
                <label className="block text-sm font-bold text-slate-700">Contraseña común (opcional)</label>
                <input
                  type="password"
                  value={defaultPassword}
                  onChange={(event) => setDefaultPassword(event.target.value)}
                  autoComplete="new-password"
                  placeholder="Se aplicará a filas sin contraseña"
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:ring-2 focus:ring-blue-500"
                />
                <div className="mt-1 text-xs text-slate-500">Al asignar una contraseña se habilita el acceso al sistema. Si se deja vacía, los empleados nuevos se crean sin acceso.</div>
              </div>

              {importRows.length > 0 && (
                <div className="mt-4 overflow-hidden rounded-xl border border-slate-200">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                      <tr><th className="px-3 py-2">Número</th><th className="px-3 py-2">Nombre</th><th className="px-3 py-2">Puesto</th><th className="px-3 py-2">Acceso</th></tr>
                    </thead>
                    <tbody>
                      {importRows.slice(0, 8).map((employee, index) => (
                        <tr key={`${employee.employee_number}-${index}`} className="border-t">
                          <td className="px-3 py-2 font-mono">{employee.employee_number || '—'}</td>
                          <td className="px-3 py-2">{employee.name || '—'}</td>
                          <td className="px-3 py-2">{employee.position || 'Sin asignar'}</td>
                          <td className="px-3 py-2">{employee.password || defaultPassword ? 'Con acceso' : 'Sin acceso'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {importRows.length > 8 && <div className="border-t bg-slate-50 px-3 py-2 text-xs text-slate-500">Vista previa de 8 de {importRows.length} filas.</div>}
                </div>
              )}

              {importResult?.parse_error && <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-800">{importResult.parse_error}</div>}
              {importResult && !importResult.parse_error && (
                <div className="mt-4 space-y-3">
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-5 text-center">
                    <div className="rounded-lg bg-slate-100 p-3"><div className="text-xl font-black">{importResult.received}</div><div className="text-xs">Recibidos</div></div>
                    <div className="rounded-lg bg-emerald-100 p-3 text-emerald-800"><div className="text-xl font-black">{importResult.created}</div><div className="text-xs">Creados</div></div>
                    <div className="rounded-lg bg-blue-100 p-3 text-blue-800"><div className="text-xl font-black">{importResult.updated}</div><div className="text-xs">Actualizados</div></div>
                    <div className="rounded-lg bg-amber-100 p-3 text-amber-800"><div className="text-xl font-black">{importResult.skipped || 0}</div><div className="text-xs">Omitidos</div></div>
                    <div className="rounded-lg bg-red-100 p-3 text-red-800"><div className="text-xl font-black">{importResult.failed}</div><div className="text-xs">Rechazados</div></div>
                  </div>
                  {importResult.errors?.length > 0 && (
                    <div className="max-h-48 overflow-y-auto rounded-lg border border-red-200">
                      {importResult.errors.map((error, index) => (
                        <div key={`${error.row}-${index}`} className="border-b border-red-100 px-3 py-2 text-sm last:border-0">
                          <span className="font-bold">Fila {error.row}{error.employee_number ? ` · ${error.employee_number}` : ''}:</span> {error.errors.join(' ')}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="mt-5 flex justify-end gap-3">
                <button type="button" onClick={closeImportModal} disabled={importing} className="rounded-lg bg-slate-200 px-4 py-2 font-semibold text-slate-700 hover:bg-slate-300 disabled:opacity-50">Cerrar</button>
                <button type="button" onClick={importEmployees} disabled={!importRows.length || importing} className="rounded-lg bg-blue-700 px-4 py-2 font-semibold text-white hover:bg-blue-800 disabled:bg-slate-300">
                  {importing ? 'Importando…' : 'Importar empleados'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default Employees;
